/**
 * PERFIL del jugador: nombre visible e icono del disco.
 *
 * El NOMBRE es de todo el mundo y vive en el dispositivo: jugar de invitado no
 * es un estado degradado, y poder llamarte como quieras sin registrarte es lo
 * que separa "una demo" de "mi partida".
 *
 * ## Los iconos van con la CUENTA, y desde la Fase C es literal
 *
 * Hasta ahora esto lo decía el comentario y lo desmentía el código: el icono
 * elegido y la lista de comprados se guardaban en `localStorage`. Eso se pierde
 * al limpiar datos, no viaja al móvil y no sobrevive a un cambio de navegador.
 * Mientras todos fueran gratis solo era molesto; en cuanto uno cueste dinero es
 * vender algo que se puede perder solo.
 *
 * Ahora manda el servidor:
 *
 *   - **qué tienes** (`entitlements`) sale de la cuenta y aquí no se escribe;
 *   - **qué llevas puesto** (`loadout.icon`) también, y cambiarlo es una
 *     petición, no una escritura local.
 *
 * `localStorage` sigue existiendo para UNA cosa: recordar la elección mientras
 * se juega de invitado y que el disco no salga pelado en cada recarga. No es la
 * verdad, es una comodidad — y en cuanto hay sesión, la cuenta la pisa.
 *
 * ## El catálogo ya no vive aquí
 *
 * Está en `@tak/accounts` (`catalog.ts`), que es un paquete sin DOM que leen los
 * dos lados. Tenerlo solo en el navegador significaba que el cliente era quien
 * decía qué existe y cuánto vale; el servidor valida contra su propia copia y no
 * se cree nada de lo que le manden. Este módulo lo reexporta para que el resto
 * de la interfaz no cambie de sitio.
 */

import {
  CATALOG, NONE, canEquip, findItem, isFree, itemsOf, priceLabel as itemPrice,
  type CatalogItem,
} from "@tak/accounts";
import { equip, equipped, owned, signedIn } from "./account";

export { NONE, priceLabel } from "@tak/accounts";
export type { CatalogItem, Slot } from "@tak/accounts";

/**
 * Un icono de disco del catálogo.
 *
 * Es el `CatalogItem` del hueco `icon`, con el nombre que ya usaba la interfaz.
 * El campo `id` sigue siendo el corto (`bolt`) porque es el que viaja por la red
 * en `PeerInfo.icon`: cambiarlo por el sku completo rompería a cualquier edge o
 * cliente que aún no se haya actualizado.
 */
export type DiscIcon = CatalogItem;

/** Catálogo de iconos, en el orden en que se enseña. */
export const DISC_ICONS: readonly DiscIcon[] = itemsOf("icon");

/** Icono por su id (o `undefined` si no está en el catálogo de esta versión). */
export function iconById(id: string | undefined): DiscIcon | undefined {
  return findItem("icon", id);
}

/** Lo que se dibuja dentro del disco para un id (vacío si no hay o no existe). */
export function iconGlyph(id: string | undefined): string {
  return iconById(id)?.glyph ?? "";
}

/**
 * ¿Tiene esta sesión acceso a los iconos?
 *
 * Sin cuenta, no: un invitado no tiene dónde guardar un icono que sea suyo de
 * verdad, y uno que se elige y luego no se puede conservar es peor que uno
 * bloqueado. Con sesión, el catálogo se abre entero (lo gratis puesto, lo de
 * pago con candado hasta que la tienda exista).
 */
export function canUseIcons(): boolean {
  return signedIn();
}

/**
 * ¿Se puede usar hoy? "Sin icono" es la ausencia de icono, no un icono, así que
 * está disponible para todo el mundo; el resto exige cuenta, y los de pago
 * además constar comprados.
 *
 * La regla de verdad es `canEquip`, la misma que aplica el servidor. Aquí solo
 * se le añade la puerta de la cuenta, que es de interfaz: sin sesión no hay
 * inventario que consultar.
 */
export function isOwned(icon: DiscIcon, ownedSkus: readonly string[] = owned()): boolean {
  if (icon.id === NONE) return true;
  if (!canUseIcons()) return false;
  return canEquip("icon", icon.id, ownedSkus);
}

/** ¿Va incluido con la cuenta, sin comprar nada? */
export { isFree };

export interface Profile {
  /** Icono puesto en el disco. */
  icon: string;
  /** Skus que constan comprados. Sale de la CUENTA; aquí nunca se inventa. */
  owned: readonly string[];
}

const KEY = "disco-football-profile";

export function defaultProfile(): Profile {
  return { icon: NONE, owned: [] };
}

/**
 * El perfil vigente.
 *
 * Con sesión, la cuenta manda entera y `localStorage` no se mira: si alguien
 * eligió un icono en este navegador antes de entrar, lo que vale es lo que su
 * cuenta diga, porque es lo que verá también en el móvil.
 *
 * Sin sesión, se recuerda la última elección local, pero pasada por el mismo
 * filtro: sin cuenta solo sobrevive "sin icono". No es un rodeo inútil — evita
 * que un perfil guardado de una sesión anterior deje puesto un icono que ahora
 * mismo no se tiene derecho a llevar.
 */
export function loadProfile(): Profile {
  if (signedIn()) {
    const skus = owned();
    const puesto = equipped("icon");
    return { icon: puesto && canEquip("icon", puesto, skus) ? puesto : NONE, owned: skus };
  }
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultProfile();
    const p = JSON.parse(raw) as Partial<Profile>;
    const icon = typeof p.icon === "string" ? p.icon : NONE;
    const chosen = iconById(icon);
    // Lo guardado NO manda sobre lo que el catálogo dice hoy: un icono retirado
    // (o uno al que ya no se tiene derecho) cae a "sin icono" en vez de dejar el
    // disco con un hueco.
    return { icon: chosen && isOwned(chosen, []) ? chosen.id : NONE, owned: [] };
  } catch {
    return defaultProfile();
  }
}

/**
 * Guarda la elección de icono.
 *
 * Con sesión va al servidor y devuelve lo que él conteste — que puede ser que
 * no. Sin sesión se recuerda en el navegador, sabiendo que es una comodidad y no
 * una propiedad.
 *
 * Es `async` a propósito, aunque la rama de invitado sea inmediata: quien llama
 * tiene que poder enterarse de que el servidor dijo que no. Una versión que
 * fingiera éxito dejaría la interfaz enseñando un icono que la cuenta no lleva.
 */
export async function saveProfile(p: Profile): Promise<{ ok: boolean; message: string }> {
  if (signedIn()) {
    return equip("icon", p.icon === NONE ? null : p.icon);
  }
  try { localStorage.setItem(KEY, JSON.stringify({ icon: p.icon })); } catch { /* storage no disponible */ }
  return { ok: true, message: "" };
}

/** Todo el catálogo, por si alguien necesita mirar más allá de los iconos. */
export { CATALOG, itemPrice };
